import type { MetaFunction } from '@remix-run/cloudflare';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { authenticate } from '../state/auth';
import { useState } from 'react';
import { TeamType, useLoginStaffMutation } from '../graphql-api';
import { Building2, KeyRound, Mail, Loader2 } from 'lucide-react';

export const meta: MetaFunction = () => {
  return [
    { title: 'Nivaas Platform' },
    { name: 'description', content: 'Nivaas - Hotel Management Platform' },
  ];
};

export default function Index() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const [, loginMutation] = useLoginStaffMutation();

  const handleLogin = async () => {
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const result = await loginMutation({
        input: {
          email,
          password,
        },
      });

      if (result?.data?.loginStaff?.team) {
        authenticate();
        const isAgent = result.data.loginStaff.team.type === TeamType.Partner;

        // Full page redirect to ensure root loader runs and populates views
        if (isAgent) {
          window.location.href = '/dashboard/agent-bookings';
        } else {
          window.location.href = '/dashboard/bookings';
        }
      } else {
        setError('Invalid email or password');
      }
    } catch {
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  return (
    <div className="flex min-h-dvh">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden items-center justify-center">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 via-violet-600 to-purple-700" />

        {/* Decorative elements */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-20 w-72 h-72 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-indigo-300/20 rounded-full blur-3xl" />
        </div>

        {/* Content - Centered */}
        <div className="relative z-10 flex flex-col items-center text-center">
          <div className="p-5 bg-white/10 rounded-3xl backdrop-blur-sm mb-6">
            <Building2 className="w-16 h-16 text-white" />
          </div>
          <h1 className="text-5xl xl:text-6xl font-bold text-white tracking-tight">
            Nivaas
          </h1>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12 bg-background">
        <div className="w-full max-w-md space-y-8">
          {/* Mobile branding */}
          <div className="lg:hidden text-center mb-8">
            <div className="inline-flex items-center gap-3 mb-4">
              <div className="p-2.5 bg-indigo-100 rounded-xl">
                <Building2 className="w-7 h-7 text-indigo-600" />
              </div>
              <h1 className="text-3xl font-bold text-foreground">Nivaas</h1>
            </div>
          </div>

          {/* Header */}
          <div className="text-center lg:text-left">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground">
              Welcome back
            </h2>
            <p className="mt-2 text-muted-foreground">Sign in to access your dashboard</p>
          </div>

          {/* Form */}
          <div className="space-y-5">
            {error && (
              <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg text-rose-700 text-sm">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-foreground font-medium">
                Email address
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  type="email"
                  id="email"
                  placeholder="you@example.com"
                  className="pl-10 h-12 bg-muted/50 border-border focus:bg-background transition-colors"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-foreground font-medium">
                Password
              </Label>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  type="password"
                  id="password"
                  placeholder="Enter your password"
                  className="pl-10 h-12 bg-muted/50 border-border focus:bg-background transition-colors"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </div>
            </div>

            <button
              onClick={handleLogin}
              disabled={isLoading}
              className="w-full h-12 bg-primary text-primary-foreground font-semibold rounded-lg hover:opacity-90 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign in'
              )}
            </button>
          </div>

          {/* Footer */}
          <p className="text-center text-xs text-muted-foreground">
            © {new Date().getFullYear()} Nivaas Platform. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}
